import React, {useEffect, useState } from "react";
import "./SocketResponse.scss";
import { ChevronDown, ChevronUp, Loader2, CircleCheck, FileText } from "lucide-react";
import ReactMarkdown from "react-markdown";

const LogResponse = ({displayedLogs = ['a','b']}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [popupData, setPopupData] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const HIDDEN_LOGS = [
    "Connecting to WebSocket",
    "WebSocket error",
    "WebSocket disconnected",
    "WebSocket connected",
    "WebSocket connection established.",
    "⚠️ Cannot emit askResponse: WebSocket not connected",
    "⚠️ Cannot emit start-logs: WebSocket not connected",
    "📤 Emitted start-logs",
    "📤 Emitted askResponse"
  ];
  
  const duplicateLogs = [...new Set(displayedLogs)].filter(
    log => !HIDDEN_LOGS.some(hidden => log.startsWith(hidden))
  );
  


  console.log("Filtered Logs:", duplicateLogs);


  const trimMessage = (message) => {
    if (!message) return "";
    return message.split("...")[0];
  };





  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
  };

  const formatSQLQuery = (query) => {
    if (!query) return '';

    // Basic SQL formatting - add line breaks after common keywords
    return query
      .replace(/\bSELECT\b/gi, '\nSELECT')
      .replace(/\bFROM\b/gi, '\nFROM')
      .replace(/\bWHERE\b/gi, '\nWHERE')
      .replace(/\bJOIN\b/gi, '\nJOIN')
      .replace(/\bLEFT JOIN\b/gi, '\nLEFT JOIN')
      .replace(/\bRIGHT JOIN\b/gi, '\nRIGHT JOIN')
      .replace(/\bINNER JOIN\b/gi, '\nINNER JOIN')
      .replace(/\bOUTER JOIN\b/gi, '\nOUTER JOIN')
      .replace(/\bGROUP BY\b/gi, '\nGROUP BY')
      .replace(/\bORDER BY\b/gi, '\nORDER BY')
      .replace(/\bHAVING\b/gi, '\nHAVING')
      .replace(/\bUNION\b/gi, '\nUNION')
      .replace(/\bAND\b/gi, '\n  AND')
      .replace(/\bOR\b/gi, '\n  OR')
      .replace(/,\s*/g, ',\n  ')
      .trim();
  };

  const detectAndFormatContent = (content) => {
    if (!content) return { content, isCode: false };

    const lowerContent = content.toLowerCase();

    // Check if it's SQL
    if (lowerContent.includes('select') || lowerContent.includes('from') ||
      lowerContent.includes('where') || lowerContent.includes('join') ||
      lowerContent.includes('insert') || lowerContent.includes('update') ||
      lowerContent.includes('delete') || lowerContent.includes('create')) {
      return {
        content: formatSQLQuery(content),
        isCode: true
      };
    }

    // Check if it's JSON
    if (content.trim().startsWith('{') && content.trim().endsWith('}')) {
      try {
        const parsed = JSON.parse(content);
        return {
          content: JSON.stringify(parsed, null, 2),
          isCode: true
        };
      } catch (e) {
        // Not valid JSON, treat as regular text
      }
    }

    // Check if it's code-like (contains common programming keywords)
    if (lowerContent.includes('function') || lowerContent.includes('class') ||
      lowerContent.includes('import') || lowerContent.includes('export') ||
      lowerContent.includes('const') || lowerContent.includes('let') ||
      lowerContent.includes('var') || lowerContent.includes('return')) {
      return {
        content: content,
        isCode: true
      };
    }

    // Default to regular text
    return {
      content: content,
      isCode: false
    };
  };

  const hasExtraData = (message) => {
    return message;
  };

  const getExtraData = (message) => {
    if (!hasExtraData(message)) return null;
    return message; // Get everything after the first 80 characters
  };


  const handleDocumentCardClick = (message) => {

    const extraData = getExtraData(message);
    console.log("Extra Data:", extraData);

    if (extraData) {
      setPopupData({
        fullMessage: message,
        extraData: extraData
      });
      setShowPopup(true);
    }
  };

  return (
    <div className="socket-res-cont pt-2">
      <div className="socket-res-content pt-2">
        <div
          className="flex items-center gap-2 text-gray-600 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2 text-gray-600">
            <CircleCheck className="w-5 h-5" />
            <span className="font-semibold">Provenance Logs</span>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>

        {/* Expandable Section */}
        {isExpanded && (
          <div className="mt-4 space-y-4 pl-5 relative">
            {duplicateLogs.map((step, index) => (
              <div key={index} className="relative flex items-start gap-3">
                {/* Connector line */}
                {index !== duplicateLogs.length - 1 && (
                  <span style={{ background: "repeating-linear-gradient(to bottom, #d3d3d3 0px, #d3d3d3 4px, transparent 4px, transparent 8px)", left: "9px" }} className="absolute top-5 w-px h-full bg-gray-300"></span>
                )}

                {/* Step Icon */}
                <CircleCheck style={{ fill: '#d3d3d3' }} className="text-white w-5 h-5 flex-shrink-0" />

                {/* Step Text */}
                <div style={{ justifyContent: "flex-start" }} className="grid gap-2">
                  <div className="text-gray-800 text-sm font-medium"> {trimMessage(step)}</div>
                  {trimMessage(step) !== "Response generated successfully." && (
                    <span>
                      <button onClick={() => handleDocumentCardClick(step)}
                        style={{ fontSize: "12px" }} className="text-gray-800 text-sm border px-2 py-1 rounded flex gap-1 align-center justify-center"><FileText className="text-gray-400" width={15} />

                        <span style={{ paddingTop: "2px" }} className="text-gray-400">Details</span>
                      </button>
                    </span>
                  )}

                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showPopup && popupData && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>📄 Additional Information</h3>
              <button className="popup-close" onClick={closePopup}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="#666"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="popup-body">
              <div className="popup-section">
                {/* <h4>Full Details:</h4> */}
                {(() => {
                  const { content, isCode } = detectAndFormatContent(popupData.fullMessage);
                  return isCode ? (
                    <pre className="popup-code">{content}</pre>
                  ) : (
                    <div className="popup-content-text">
                      <ReactMarkdown className="summary-text line-clamp-3">
                        {content}
                      </ReactMarkdown>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="popup-footer">
              <button className="popup-button secondary" onClick={closePopup}>
                Close
              </button>
              <button
                className="popup-button primary"
                onClick={() => {
                  navigator.clipboard.writeText(popupData.fullMessage).then(() => {
                    alert('Content copied to clipboard!');
                  }).catch(() => {
                    alert('Failed to copy to clipboard');
                  });
                }}
              >
                Copy Content
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogResponse;
